/**
 * Admin V3 — Financial / Payouts — top-level page
 *
 * Mounted at: /admin-v3/financial/payouts (see AdminV3.js)
 *
 * GOVERNANCE — STRICT FINANCIAL-CORE READ-ONLY:
 *   - Canonical source of truth: `payouts` collection (per-booking accrual
 *     records). 12+ backend services treat this as authoritative.
 *   - DISTINCT from withdrawals — payouts are platform→driver accruals;
 *     withdrawals are driver→bank transfer requests. Different domain
 *     entities with different lifecycles and operator audiences.
 *
 * AUTHORIZED ENDPOINT (exactly one):
 *   GET /admin/control-tower/financial/payouts (RBAC ADMIN_FINANCIAL_PAYOUTS)
 *
 * Explicitly NOT consumed:
 *   - /admin/economy/payouts (legacy endpoint returning withdrawals as payouts —
 *     structurally misleading; deprecation backlog item recorded)
 *   - Any withdrawal endpoint
 *   - Any /api/driver/economy/* endpoint
 *   - Any intervention API surface
 *
 * Defensive UX:
 *   - All 8 status values across PayoutState (6) + PayoutStatus (4) render
 *     correctly. Both `paid` (canonical) and `paid_out` (legacy) → "Versé".
 *   - Truncated Stripe Transfer ID in main row, full in expanded detail.
 *   - External provider account identifiers omitted entirely.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Coins } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import FinancialPayoutsSearchBar from './FinancialPayoutsSearchBar';
import FinancialPayoutsTable from './FinancialPayoutsTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 100;

const FinancialPayoutsPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');
  const [driverId, setDriverId] = useState('');
  const [debouncedDriverId, setDebouncedDriverId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedDriverId(driverId.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [driverId]);

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (debouncedDriverId) queryParams.driver_id = debouncedDriverId;
  if (status)            queryParams.status    = status;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/control-tower/financial/payouts',
    queryParams
  );

  const allPayouts = Array.isArray(data?.payouts) ? data.payouts : [];

  // Client-side search over loaded page (payout_id, booking_id, driver_id)
  const payouts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allPayouts;
    return allPayouts.filter((p) =>
      (p.payout_id || p.id || '').toLowerCase().includes(q) ||
      (p.booking_id || '').toLowerCase().includes(q) ||
      (p.driver_id || '').toLowerCase().includes(q)
    );
  }, [allPayouts, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(debouncedDriverId) || Boolean(status);
  const isAtBackendCap = allPayouts.length === DEFAULT_LIMIT;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('financial.payouts.title', locale)}
      icon={Coins}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        {/* Read-only governance subtitle (italic, visible at all times) */}
        <p
          className="text-[11px] italic text-slate-400 -mt-2"
          data-testid="adminv3-financial-payouts-subtitle"
        >
          {t('financial.payouts.subtitle', locale)}
        </p>

        <FinancialPayoutsSearchBar
          status={status}
          onStatusChange={setStatus}
          driverId={driverId}
          onDriverIdChange={setDriverId}
          search={search}
          onSearchChange={setSearch}
          resultsCount={payouts.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <FinancialPayoutsTable
          payouts={payouts}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-financial-payouts-cap-hint"
          >
            {t('financial.payouts.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default FinancialPayoutsPage;
